"use client";
import styled from "@emotion/styled";
import { readableColor, rem } from "polished";
import { CSSProperties } from "react";

import { Icon } from "../../atoms/Icon";
import { colors } from "../../design-tokens/colors";
import { globalTextMaxWidth, sizes } from "../../design-tokens/dimensions";
import { borderRadii, boxShadows } from "../../design-tokens/effects";
import { typography } from "../../design-tokens/typography";
import type { AlertLevel, AlertSize } from "./types";

/**
 * Gets the background colour for the given variant string
 * @param variant - Notification variant
 */
const getBackgroundColor = (
  variant: AlertLevel
): Exclude<CSSProperties["backgroundColor"], undefined> => {
  switch (variant) {
    case "success":
      return colors.green;
    case "warning":
      return colors.orange;
    case "error":
      return colors.red;
    default:
      return colors.blue;
  }
};

/**
 * Gets a suitable text colour that contrasts with the background
 * @param level - visual level of the Alert
 */
export const getTextColor = (level: AlertLevel): Exclude<CSSProperties["color"], undefined> => {
  return readableColor(getBackgroundColor(level), colors.universalBase, colors.white, true);
};

export const Root = styled.div<{
  $level: AlertLevel;
  $entitySize: AlertSize;
}>`
  align-items: stretch;
  background-color: ${({ $level }) => getBackgroundColor($level)};
  border-radius: ${({ $entitySize }) => ($entitySize === "m" ? borderRadii.r8 : 0)};
  box-shadow: ${boxShadows.frame};
  color: ${({ $level }) => getTextColor($level)};
  display: flex;
  font-size: ${({ $entitySize }) =>
    $entitySize === "m" ? typography.sizes["18"] : typography.sizes["16"]};
  gap: ${sizes.s32.rem};
  margin: 0 auto;
  max-width: ${rem(globalTextMaxWidth)};
  padding: ${({ $entitySize }) => ($entitySize === "m" ? sizes.s16.rem : sizes.s12.rem)};
  position: relative;

  a {
    color: ${({ $level }) => getTextColor($level)};
    font-size: inherit;
    font-weight: ${typography.weights.bold};

    &:hover {
      text-decoration: underline;
    }
  }
`;

export const Inner = styled.div`
  align-items: center;
  display: flex;
  overflow: hidden;
  width: 100%;
  word-wrap: break-word;
`;

export const AlertIcon = styled(Icon)`
  aspect-ratio: 1;
  width: ${sizes.s16.rem};
`;
